import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import CashSessionService, { ICashSessionStatus, IStartCashSessionData, IActiveSessionResponse } from '@/services/pos/cashSessionService';
import { SessionRecoveryModal } from '@/components/features/SessionRecoveryModal';
import { useAuth } from '@/hooks/useAuth';
import { formatCurrency } from '@/lib/utils';

interface CashSessionManagerProps {
  onSessionChange?: (session: ICashSessionStatus) => void;
  posId?: number; // ID del POS seleccionado
}

export const CashSessionManager: React.FC<CashSessionManagerProps> = ({ onSessionChange, posId }) => {
  const { user } = useAuth();
  const [sessionStatus, setSessionStatus] = useState<ICashSessionStatus>({ 
    isOpen: false, 
    sessionId: null, 
    cajeroId: null, 
    efectivoDeclarado: 0, 
    efectivoCalculado: 0, 
    inicioTurno: null 
  });
  
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [activeSessionData, setActiveSessionData] = useState<IActiveSessionResponse['data']['activeSession'] | null>(null);
  const [efectivoDeclarado, setEfectivoDeclarado] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    loadSessionStatus();
    
    // Sincronizar con el backend al cargar
    syncWithBackendOnLoad();
    
    // Escuchar cambios en localStorage
    const handleStorageChange = () => {
      loadSessionStatus();
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const syncWithBackendOnLoad = async () => {
    try {
      await CashSessionService.syncWithBackend();
      loadSessionStatus(); // Recargar después de sincronizar
    } catch (error) {
      console.warn('Error sincronizando con backend al cargar:', error);
    }
  };

  const loadSessionStatus = () => {
    const currentSession = CashSessionService.getCurrentSession();
    setSessionStatus(currentSession);
    onSessionChange?.(currentSession);
  };

  const handleStartSession = async () => {
    if (!user?.id) {
      setError('Usuario no autenticado');
      return;
    }

    if (!posId) {
      setError('Debe seleccionar un punto de venta antes de iniciar sesión');
      return;
    }

    // Verificar si hay una sesión activa antes de crear una nueva
    await checkForActiveSession();
  };

  const checkForActiveSession = async () => {
    if (!user?.id) return;

    setIsLoading(true);
    setError('');

    try {
      const response = await CashSessionService.checkActiveSession(user.id);
      
      if (response.data.hasActiveSession && response.data.activeSession) {
        // Hay una sesión activa, mostrar modal de recuperación
        setActiveSessionData(response.data.activeSession);
        setShowRecoveryModal(true);
        setShowStartDialog(false);
      } else {
        // No hay sesión activa, proceder normalmente
        await createNewSession();
      }
    } catch (error: any) {
      // Si hay error verificando, asumir que no hay sesión activa y continuar
      console.warn('Error verificando sesión activa, continuando con nueva sesión:', error);
      await createNewSession();
    } finally {
      setIsLoading(false);
    }
  };

  const createNewSession = async () => {
    if (!user?.id || !posId) return;

    setIsLoading(true);
    setError('');

    try {
      const sessionData: IStartCashSessionData = {
        cajeroId: user.id,
        efectivoDeclarado: efectivoDeclarado,
        posId: posId || 1,
      };

      await CashSessionService.startSession(sessionData);
      loadSessionStatus();
      setShowStartDialog(false);
      setEfectivoDeclarado(0);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Error al iniciar sesión de caja');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecoverSession = async () => {
    if (!activeSessionData) return;

    setIsLoading(true);
    setError('');

    try {
      await CashSessionService.recoverActiveSession(activeSessionData.id);
      loadSessionStatus();
      setShowRecoveryModal(false);
      setActiveSessionData(null);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Error al recuperar sesión activa');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForceCloseAndCreateNew = async () => {
    if (!activeSessionData || !user?.id || !posId) return;

    setIsLoading(true);
    setError('');

    try {
      // 1. Forzar cierre de la sesión anterior
      await CashSessionService.forceCloseActiveSession(activeSessionData.id);
      
      // 2. Crear nueva sesión
      const startData: IStartCashSessionData = {
        cajeroId: user.id,
        efectivoDeclarado: efectivoDeclarado,
        posId: posId,
      };

      await CashSessionService.startSession(startData);
      loadSessionStatus();
      setShowRecoveryModal(false);
      setActiveSessionData(null);
      setShowStartDialog(false);
      setEfectivoDeclarado(0);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Error al cerrar sesión anterior y crear nueva');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSession = async () => {
    if (!sessionStatus.sessionId) {
      setError('No hay sesión activa para cerrar');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await CashSessionService.closeSession(sessionStatus.sessionId);
      loadSessionStatus();
      setShowCloseDialog(false);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Error al cerrar sesión de caja');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('es-CL');
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          Gestión de Sesión de Caja
          <Badge variant={sessionStatus.isOpen ? "default" : "secondary"}>
            {sessionStatus.isOpen ? "ABIERTA" : "CERRADA"}
          </Badge>
        </CardTitle>
        <CardDescription>
          Control de turnos y efectivo en caja
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {sessionStatus.isOpen ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-3 bg-muted rounded-lg">
                <Label className="text-sm font-medium">Efectivo Declarado</Label>
                <p className="text-lg font-semibold text-green-600">
                  {formatCurrency(sessionStatus.efectivoDeclarado)}
                </p>
              </div>
              
              <div className="p-3 bg-muted rounded-lg">
                <Label className="text-sm font-medium">Efectivo Calculado</Label>
                <p className="text-lg font-semibold text-blue-600">
                  {formatCurrency(sessionStatus.efectivoCalculado)}
                </p>
              </div>
              
              <div className="p-3 bg-muted rounded-lg">
                <Label className="text-sm font-medium">Diferencia</Label>
                <p className={`text-lg font-semibold ${
                  sessionStatus.efectivoCalculado - sessionStatus.efectivoDeclarado >= 0 
                    ? 'text-green-600' 
                    : 'text-red-600'
                }`}>
                  {formatCurrency(sessionStatus.efectivoCalculado - sessionStatus.efectivoDeclarado)}
                </p>
              </div>
            </div>

            <div className="p-3 bg-muted rounded-lg">
              <Label className="text-sm font-medium">Turno Iniciado</Label>
              <p className="text-sm">{formatTime(sessionStatus.inicioTurno)}</p>
            </div>

            <Button 
              onClick={() => setShowCloseDialog(true)}
              variant="destructive"
              className="w-full"
              disabled={isLoading}
            >
              Cerrar Sesión de Caja
            </Button>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-4">
              No hay una sesión de caja activa
            </p>
            <Button 
              onClick={() => setShowStartDialog(true)}
              className="w-full md:w-auto"
              disabled={isLoading}
            >
              Iniciar Sesión de Caja
            </Button>
          </div>
        )}

        {/* Dialog para iniciar sesión */}
        <Dialog open={showStartDialog} onOpenChange={setShowStartDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Iniciar Sesión de Caja</DialogTitle>
              <DialogDescription>
                Declare el efectivo inicial en caja para comenzar su turno
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="efectivo">Efectivo Declarado</Label>
                <Input
                  id="efectivo"
                  type="number"
                  step="0.01"
                  min="0"
                  value={efectivoDeclarado}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEfectivoDeclarado(parseFloat(e.target.value) || 0)}
                  placeholder="0.00"
                />
              </div>
              
              <div className="flex justify-end gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowStartDialog(false)}
                  disabled={isLoading}
                >
                  Cancelar
                </Button>
                <Button 
                  onClick={handleStartSession}
                  disabled={isLoading}
                >
                  {isLoading ? 'Iniciando...' : 'Iniciar Sesión'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog para cerrar sesión */}
        <Dialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cerrar Sesión de Caja</DialogTitle>
              <DialogDescription>
                ¿Está seguro que desea cerrar la sesión de caja actual?
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <h4 className="font-medium mb-2">Resumen de la Sesión</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Efectivo Declarado:</span>
                    <span>{formatCurrency(sessionStatus.efectivoDeclarado)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Efectivo Calculado:</span>
                    <span>{formatCurrency(sessionStatus.efectivoCalculado)}</span>
                  </div>
                  <div className="flex justify-between font-medium">
                    <span>Diferencia:</span>
                    <span className={
                      sessionStatus.efectivoCalculado - sessionStatus.efectivoDeclarado >= 0 
                        ? 'text-green-600' 
                        : 'text-red-600'
                    }>
                      {formatCurrency(sessionStatus.efectivoCalculado - sessionStatus.efectivoDeclarado)}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowCloseDialog(false)}
                  disabled={isLoading}
                >
                  Cancelar
                </Button>
                <Button 
                  variant="destructive"
                  onClick={handleCloseSession}
                  disabled={isLoading}
                >
                  {isLoading ? 'Cerrando...' : 'Cerrar Sesión'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal de Recuperación de Sesión */}
        <SessionRecoveryModal
          isOpen={showRecoveryModal}
          onClose={() => {
            setShowRecoveryModal(false);
            setActiveSessionData(null);
          }}
          activeSessionData={activeSessionData}
          onRecoverSession={handleRecoverSession}
          onForceCloseAndCreateNew={handleForceCloseAndCreateNew}
          isLoading={isLoading}
        />
      </CardContent>
    </Card>
  );
};

export default CashSessionManager;
